import { useEffect, useState } from "react";
import { Users, X, Menu, Link2, List } from "lucide-react";

import { INTERACTION_TYPES } from "../consts";
import { callApi, fmtDate, formatDT } from "../utils";
import Badge from "./badge";
import EditGroupModal from "./edit-group-modal";

export default function OverviewTab({
  group,
  getGroups,
  practicals,
  getPracticals,
  allTeachers,
  editOpen,
  closeModal,
}) {
  const [linkedTeachers, setLinkedTeachers] = useState([]);
  const [subgroups, setSubgroups] = useState([]);
  const [subgroupVariants, setSubgroupVariants] = useState([]);
  const [studentVariants, setStudentVariants] = useState([]);
  const [changeLog, setChangeLog] = useState([]);
  const [toLink, setToLink] = useState("");

  const isGroup = group.interactionType === INTERACTION_TYPES.group;

  const getLinkedTeachers = () => {
    setToLink("");
    setLinkedTeachers([]);

    callApi({
      method: "GET",
      path: `api/teacher/practice-groups/${group.id}/linked-teachers`,
      needAuth: true,
    }).then((response) => {
      const err = response.error;
      if (err) {
        console.error(err);
      } else {
        const respArr = response.response;
        if (respArr) {
          setLinkedTeachers(respArr);
        } else {
          console.error(respArr);
        }
      }
    });
  };

  const getGroupDetails = () => {
    callApi({
      method: "GET",
      path: `api/teacher/practice-groups/${group.id}/change-log`,
      needAuth: true,
    }).then((response) => {
      const err = response.error;
      if (err) {
        console.error(err);
      } else {
        const respArr = response.response;
        if (respArr) {
          setChangeLog(respArr);
        } else {
          console.error(respArr);
        }
      }
    });

    if (!isGroup) {
      callApi({
        method: "GET",
        path: `api/teacher/practice-groups/${group.id}/student-variants`,
        needAuth: true,
      }).then((response) => {
        const err = response.error;
        if (err) {
          console.error(err);
        } else {
          const respArr = response.response;
          if (respArr) {
            setStudentVariants(respArr);
          } else {
            console.error(respArr);
          }
        }
      });

      return;
    }

    callApi({
      method: "GET",
      path: `api/teacher/practice-groups/${group.id}/subgroups`,
      needAuth: true,
    }).then((response) => {
      const err = response.error;
      if (err) {
        console.error(err);
      } else {
        const respArr = response.response;
        if (respArr) {
          setSubgroups(respArr);
        } else {
          console.error(respArr);
        }
      }
    });

    callApi({
      method: "GET",
      path: `api/teacher/practice-groups/${group.id}/subgroup-variants`,
      needAuth: true,
    }).then((response) => {
      const err = response.error;
      if (err) {
        console.error(err);
      } else {
        const respArr = response.response;
        if (respArr) {
          setSubgroupVariants(respArr);
        } else {
          console.error(respArr);
        }
      }
    });
  };

  useEffect(() => {
    setSubgroups([]);
    setStudentVariants([]);
    setSubgroupVariants([]);
    setChangeLog([]);

    getLinkedTeachers();
    getGroupDetails();
  }, [group.id]);

  const addLinked = () => {
    if (!toLink) return;

    callApi({
      method: "POST",
      path: `api/teacher/practice-groups/${group.id}/linked-teachers/${toLink}`,
      needAuth: true,
    }).then((response) => {
      if (response.error) {
        console.error(response.error);
      } else {
        getLinkedTeachers();
      }
    });
  };

  const removeLinked = (id) => {
    callApi({
      method: "DELETE",
      path: `api/teacher/practice-groups/${group.id}/linked-teachers/${id}`,
      needAuth: true,
    }).then((response) => {
      if (response.error) {
        console.error(response.error);
      } else {
        getLinkedTeachers();
      }
    });
  };

  const renderLinkedTeachers = () => {
    if (!linkedTeachers.length) {
      return <span className="muted small">Пока никто не связан.</span>;
    }

    return linkedTeachers.map((teacher) => {
      return (
        <span className="chip" key={teacher.id}>
          {teacher.fullName}
          {group.owner && (
            <button
              onClick={() => removeLinked(teacher.id)}
              aria-label="Удалить"
            >
              <X size={12} />
            </button>
          )}
        </span>
      );
    });
  };

  const renderLinkTeachers = () => {
    if (!group.owner) {
      return (
        <p className="muted small">
          Управлять связанными преподавателями может только владелец группы.
        </p>
      );
    }

    const linkable = allTeachers.filter(
      (t) =>
        // t.id !== group.ownerId &&
        !linkedTeachers.some((elem) => elem.id === t.id),
    );

    if (linkable.length) {
      return (
        <div className="inline-add">
          <select value={toLink} onChange={(e) => setToLink(e.target.value)}>
            <option value="">Выберите преподавателя</option>
            {linkable.map((t) => (
              <option key={t.id} value={t.id}>
                {t.fullName}
              </option>
            ))}
          </select>
          <button className="btn small" onClick={addLinked}>
            Связать
          </button>
        </div>
      );
    }
  };

  const getPracticeHeader = (arr) =>
    arr.map((p) => <th key={p.number}>{`Работа ${p.number}`}</th>);

  const getVariantsColumns = (arr) =>
    arr.map((p) => (
      <td key={p.number}>{p.variant ? `Вариант ${p.variant}` : ""}</td>
    ));

  const getSubgroupVariantsMappingColumns = () =>
    subgroupVariants.map((v) => (
      <tr key={v.subgroupNumber}>
        <td>{`Подгруппа ${v.subgroupNumber}`}</td>
        {getVariantsColumns(v.practices)}
      </tr>
    ));

  const getStudentVariantsMappingColumns = () =>
    studentVariants.map((v) => (
      <tr key={v.studentId}>
        <td>{v.fullName}</td>
        {getVariantsColumns(v.practices)}
      </tr>
    ));

  return (
    <>
      <div className="panel-grid">
        <div className="card">
          <h4>
            <Menu size={15} /> Список практических работ
          </h4>
          <table className="data-table">
            <thead>
              <tr>
                <th>№</th>
                <th>Начало</th>
                <th>Завершение</th>
                <th>Доработка</th>
                <th>Статус</th>
                <th>Кол-во вариантов</th>
              </tr>
            </thead>
            <tbody>
              {practicals.map((p) => {
                return (
                  <tr key={p.number}>
                    <td className="num-cell">{p.number}</td>
                    <td>{fmtDate(p.startDate)}</td>
                    <td>{fmtDate(p.endDate)}</td>
                    <td>{fmtDate(p.reworkEndDate)}</td>
                    <td>
                      <Badge status={p.status} />
                    </td>
                    <td>{p.variantsCount}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {isGroup && (
          <div className="card">
            <h4>
              <Users size={15} /> Подгруппы
            </h4>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Студент</th>
                  <th>Подгруппа</th>
                </tr>
              </thead>
              <tbody>
                {subgroups.map((s) => (
                  <tr key={s.studentId}>
                    <td>{s.fullName}</td>
                    <td>Подгруппа {s.subgroupNumber}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="card">
          <h4>
            <Link2 size={15} /> Связанные преподаватели
          </h4>
          <p className="muted small">
            Получают доступ ко всем материалам, отчётам и исходным кодам группы.
          </p>
          <div className="chip-row">{renderLinkedTeachers()}</div>
          {renderLinkTeachers()}
        </div>
      </div>

      <div className="cards">
        {subgroupVariants.length > 0 && (
          <div className="card">
            <h4>
              <List size={15} /> Распределение вариантов по подгруппам
            </h4>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Подгруппа</th>
                  {getPracticeHeader(subgroupVariants[0].practices)}
                </tr>
              </thead>
              <tbody>{getSubgroupVariantsMappingColumns()}</tbody>
            </table>
          </div>
        )}
        {studentVariants.length > 0 && (
          <div className="card">
            <h4>
              <List size={15} /> Распределение вариантов по студентам
            </h4>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Студент</th>
                  {getPracticeHeader(studentVariants[0].practices)}
                </tr>
              </thead>
              <tbody>{getStudentVariantsMappingColumns()}</tbody>
            </table>
          </div>
        )}
        <div className="card">
          <h4>Журнал изменений</h4>
          {changeLog.length ? (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Номер практики</th>
                  <th>Параметр</th>
                  <th>Прежнее значение</th>
                  <th>Новое значение</th>
                  <th>Кем изменено</th>
                  <th>Дата и время</th>
                </tr>
              </thead>
              <tbody>
                {changeLog.map((entry) => (
                  <tr key={entry.id}>
                    <td>{entry.practicalNumber ?? "—"}</td>
                    <td>{entry.parameter ?? "—"}</td>
                    <td>{entry.oldValue ?? "—"}</td>
                    <td>{entry.newValue ?? "—"}</td>
                    <td>{entry.changedBy ?? "—"}</td>
                    <td>{formatDT(entry.at) ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="muted small">Изменений пока нет.</p>
          )}
        </div>
      </div>

      {editOpen && (
        <EditGroupModal
          group={group}
          practicals={practicals}
          subgroups={subgroups}
          subgroupVariants={subgroupVariants}
          studentVariants={studentVariants}
          onCancel={closeModal}
          onSaved={() => {
            closeModal();
            getPracticals();
            getGroupDetails();
            getGroups();
          }}
        />
      )}
    </>
  );
}
